import { CustomSpaceTypeModalCard } from 'spw-components/dialogs/custom-space-types/styles';
import { Dialog, DialogContent } from '@mui/material';
import { isEmpty } from 'ramda';
import { ErrorMessage } from 'spw-components/dialogs/styles';
import {
  SPWCancelButton88Width,
  SPWDialogActions,
  SPWSaveButton88Width,
} from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useEnterShortcut } from 'spw-esa-projects/building-details/floorplan/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { updateProjectSpaceTypeCategory } from 'spw-redux/modules/project-space-type-categories/actions';
import { selectSelectedSpaceTypeCategory } from 'spw-redux/modules/worksheet/selectors';
import { selectProjectSpaceTypeCategories } from 'spw-redux/modules/projects/selectors';
import { WorksheetInput } from 'spw-components/worksheet/styles';
import { CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH } from 'spw-programming-projects/constants';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import { isDuplicateCategory } from 'spw-components/worksheet/utils';

type Props = {
  isOpen: boolean;
  onDismiss: (name?: string, clientFacingName?: string) => void;
};

const InputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  font-family: Roboto;
`;

const EditCategoryNameDialog = ({ isOpen, onDismiss }: Props) => {
  const dispatch = useDispatch();
  const spaceTypeCategories = useSelector(selectProjectSpaceTypeCategories);
  const spaceTypeCategory = useSelector(
    selectSelectedSpaceTypeCategory
  ) as IProjectSpaceTypeCategory;

  const [newName, setNewName] = useState<string>();
  const [initialCategoryName] = useState(() => {
    return spaceTypeCategory?.name ?? '';
  });
  const [categoryNameError, setCategoryNameError] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  useEffect(() => {
    setNewName(spaceTypeCategory?.name);
  }, [spaceTypeCategory]);

  const allSubcategories = useMemo(() => {
    return spaceTypeCategories.reduce((subcategories, cat) => {
      return [...subcategories, ...(cat.subcategories || [])];
    }, []);
  }, [spaceTypeCategories]);

  const handleCategoryUpdate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const categoryName = event.target.value.substring(0, 22);
      if (
        categoryName.trim() &&
        isDuplicateCategory(
          spaceTypeCategories,
          allSubcategories,
          spaceTypeCategory.id,
          categoryName
        )
      ) {
        setCategoryNameError(true);
      } else {
        setCategoryNameError(false);
      }
      setNewName(categoryName);
    },
    [spaceTypeCategories, spaceTypeCategory, allSubcategories]
  );

  const saveButtonIsEnabled = useMemo(() => {
    if (categoryNameError) return false;

    const cleanName = newName?.trim();

    return !isEmpty(cleanName) && isFormDirty;
  }, [newName, categoryNameError, isFormDirty]);

  const handleSave = () => {
    if (!saveButtonIsEnabled) return;

    const cleanName = newName.trim();
    dispatch(
      updateProjectSpaceTypeCategory({
        ...spaceTypeCategory,
        name: cleanName,
      })
    );
    onDismiss();
    setNewName('');
  };

  useEnterShortcut({ onEnterKeyPressed: handleSave, enabled: isOpen });

  useEffect(() => {
    setIsFormDirty(newName !== initialCategoryName);
  }, [newName, initialCategoryName]);

  const categoryType = useMemo(() => {
    return spaceTypeCategory?.parentId ? 'Subcategory' : 'Space Category';
  }, [spaceTypeCategory]);

  return (
    <Dialog
      disableScrollLock
      open={isOpen}
      PaperComponent={CustomSpaceTypeModalCard}
      onClose={() => onDismiss()}
    >
      <div id="alert-dialog-title" className="title">
        Rename {categoryType}
      </div>
      <DialogContent className="content">
        <InputLabel>{categoryType} Name</InputLabel>
        <div
          style={{
            width: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
            minWidth: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
          }}
        >
          <WorksheetInput
            multiline
            variant="outlined"
            data-testid="internal-name-input"
            value={newName}
            onChange={handleCategoryUpdate}
          />
        </div>

        <ErrorMessage
          className="error-message"
          style={{
            opacity: categoryNameError ? 1 : 0,
          }}
        >
          {categoryType} name already exists.
        </ErrorMessage>
      </DialogContent>
      <SPWDialogActions>
        <SPWSaveButton88Width
          data-testid="create-space-type-save"
          onClick={handleSave}
          disabled={!saveButtonIsEnabled}
          type="submit"
        />
        <SPWCancelButton88Width
          customColor={colors.black}
          onClick={() => {
            onDismiss();
            setNewName('');
          }}
        />
      </SPWDialogActions>
    </Dialog>
  );
};

export default EditCategoryNameDialog;
